import { useState, useContext, createContext } from "react"
import { string, func, node, bool } from "prop-types"
import { Box, Button, Modal as TapestryModal, StackView } from "@planningcenter/tapestry-react"
import { StatusMessage } from "components/ui"

export const ModalContext = createContext()

export const ModalContextProvider = ({ children }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const onModalOpen = () => setModalOpen(true)

  const onModalClose = () => {
    setError(null)
    setSaving(false)
    setModalOpen(false)
  }

  const onSaving = () => {
    setSaving(true)
    setError(null)
  }

  const onError = error => {
    setError(error || "Changes not saved")
    setSaving(false)
  }

  return (
    <ModalContext.Provider
      value={{ modalOpen, saving, error, onModalOpen, onModalClose, onSaving, onError }}
    >
      {children}
    </ModalContext.Provider>
  )
}

ModalContextProvider.propTypes = {
  children: node.isRequired
}

export const useModal = () => {
  const context = useContext(ModalContext)

  if (context === undefined) {
    throw new Error("Modal and ModalTrigger must be used within a ModalContextProvider")
  }

  return context
}

export const ModalTrigger = buttonProps => {
  const { onModalOpen } = useModal()

  return <Button onClick={onModalOpen} {...buttonProps} />
}

export const Modal = ({
  children,
  onModalCancel = null,
  onModalSubmit,
  title,
  showSubmitButton = true
}) => {
  const { modalOpen, saving, error, onModalClose } = useModal()

  const handleCancel = () => (onModalCancel ? onModalCancel() : onModalClose())

  return (
    <TapestryModal
      open={modalOpen}
      borderRadius={1}
      paddingTop={4}
      paddingRight={0}
      paddingBottom={0}
      paddingLeft={0}
    >
      <StackView className="modal__header" axis="horizontal" distribution="space-between">
        <Box className="modal-title">{title}</Box>
        <Button
          variant="naked"
          title="Close modal"
          icon={{ name: "general.x" }}
          onClick={onModalClose}
          disabled={saving}
        />
      </StackView>
      <StackView className="modal__body">{children}</StackView>
      <StackView
        axis="horizontal"
        spacing={1}
        alignment="center"
        distribution="end"
        className="modal__footer"
      >
        {error && <StatusMessage icon="general#exclamation-triangle" text={error} type="error" />}
        <Button title="Cancel" variant="naked" onClick={handleCancel} disabled={saving} />
        {showSubmitButton && (
          <Button
            title="Save"
            theme="create"
            onClick={onModalSubmit}
            spinner={saving}
            disabled={saving}
          />
        )}
      </StackView>
    </TapestryModal>
  )
}

Modal.propTypes = {
  onModalCancel: func,
  onModalSubmit: func.isRequired,
  title: string.isRequired,
  children: node.isRequired,
  showSubmitButton: bool
}
